/**
 * Sourcing Intelligence tab — per-article Category-Manager-style analysis
 * (bullish/bearish, magnitude, time-horizon, action trigger, detailed
 * analysis, recommendations). One card per article.
 */

import { memo, useMemo } from 'react';
import { AlertTriangle, Briefcase, Loader2, RefreshCw } from 'lucide-react';
import Button from '../../ui/Button';
import { SourcingInsightCard } from './SourcingInsightCard';
import {
  useCompanySourcingAnalysis,
  type UseCompanySourcingAnalysisOptions,
} from './useCompanySourcingAnalysis';
import type { SourcingAnalysisResponse } from './types';

interface SourcingIntelligenceTabProps {
  companyName: string;
  defaultDaysBack?: UseCompanySourcingAnalysisOptions['defaultDaysBack'];
}

const DAYS_BACK_OPTIONS = [7, 30, 90] as const;

function StatBox({ label, value, tone = 'neutral' }: { label: string; value: string | number; tone?: string }) {
  const toneClass =
    tone === 'green'
      ? 'text-emerald-700'
      : tone === 'red'
      ? 'text-rose-700'
      : tone === 'amber'
      ? 'text-amber-700'
      : 'text-neutral-900';
  return (
    <div className="rounded-md border border-neutral-100 bg-white px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500">{label}</p>
      <p className={`mt-0.5 text-base font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

function Header({
  companyName,
  data,
  daysBack,
  onDaysBackChange,
  onRefresh,
  isLoading,
  isRefreshing,
}: {
  companyName: string;
  data: SourcingAnalysisResponse | null;
  daysBack: number;
  onDaysBackChange: (d: 7 | 30 | 90) => void;
  onRefresh: () => void;
  isLoading: boolean;
  isRefreshing: boolean;
}) {
  const analysis = data?.analysis ?? null;
  const bullishCount = analysis?.direction_breakdown?.bullish ?? 0;
  const bearishCount = analysis?.direction_breakdown?.bearish ?? 0;
  const neutralCount = analysis?.direction_breakdown?.neutral ?? 0;

  return (
    <div className="sticky top-0 z-30 border-b border-neutral-100 bg-white/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h2 className="truncate text-base font-bold leading-tight text-neutral-900">
            {companyName} <span className="text-neutral-400">— Sourcing Intelligence</span>
          </h2>
          {!isLoading && analysis && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-600">
              <span>
                Analysed {analysis.analyzed_count}/{data?.articles_fetched ?? 0} articles
              </span>
              {bullishCount > 0 && <span className="text-emerald-700">· {bullishCount} bullish</span>}
              {bearishCount > 0 && <span className="text-rose-700">· {bearishCount} bearish</span>}
              {neutralCount > 0 && <span className="text-neutral-500">· {neutralCount} neutral</span>}
              {analysis.avg_confidence != null && (
                <span className="text-neutral-500">
                  · Avg confidence {Math.round(analysis.avg_confidence * 100)}%
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 rounded-lg border border-neutral-200 bg-neutral-50 p-0.5">
            {DAYS_BACK_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onDaysBackChange(opt)}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold transition ${
                  daysBack === opt
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-neutral-500 hover:text-neutral-800'
                }`}
                aria-pressed={daysBack === opt}
              >
                {opt}d
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading || isRefreshing}>
            {isRefreshing || isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onRefresh }: { onRefresh: () => void }) {
  return (
    <div className="py-16 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-500">
        <Briefcase className="h-5 w-5" />
      </div>
      <p className="text-sm font-medium text-neutral-700">No sourcing signals found</p>
      <p className="mt-1 text-xs text-neutral-400">
        Try widening the time range or check back later.
      </p>
      <Button variant="outline" size="sm" onClick={onRefresh} className="mt-4">
        <RefreshCw className="h-4 w-4" />
        Try again
      </Button>
    </div>
  );
}

function ErrorState({ message, onRefresh }: { message: string; onRefresh: () => void }) {
  return (
    <div className="py-12 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500">
        <AlertTriangle className="h-5 w-5" />
      </div>
      <p className="text-sm font-medium text-red-700">{message}</p>
      <Button variant="outline" size="sm" onClick={onRefresh} className="mt-4">
        <RefreshCw className="h-4 w-4" />
        Retry
      </Button>
    </div>
  );
}

function SourcingIntelligenceTabBase({
  companyName,
  defaultDaysBack = 90,
}: SourcingIntelligenceTabProps) {
  const {
    data,
    isLoading,
    isRefreshing,
    error,
    daysBack,
    setDaysBack,
    refresh,
  } = useCompanySourcingAnalysis({ companyName, defaultDaysBack });

  // Analyzed first, then skipped (so the actionable cards lead)
  const sortedArticles = useMemo(() => {
    const all = data?.analysis?.articles ?? [];
    return [...all].sort((a, b) => {
      if (a.status === b.status) return 0;
      return a.status === 'analyzed' ? -1 : 1;
    });
  }, [data]);

  const showEmpty =
    !isLoading && !error && !!data &&
    (data.status === 'no_articles' || sortedArticles.length === 0);

  return (
    <div>
      <Header
        companyName={companyName}
        data={data}
        daysBack={daysBack}
        onDaysBackChange={setDaysBack}
        onRefresh={refresh}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
      />

      <div className="space-y-4 px-6 pt-5">
        {error && <ErrorState message={error} onRefresh={refresh} />}

        {!error && isLoading && (
          <div className="flex items-center justify-center py-16 text-neutral-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            <span className="text-sm">Analysing news for {companyName}…</span>
          </div>
        )}

        {!error && !isLoading && showEmpty && <EmptyState onRefresh={refresh} />}

        {!error && !isLoading && data && !showEmpty && sortedArticles.map((article) => (
          <SourcingInsightCard key={article.article_url} article={article} />
        ))}
      </div>
    </div>
  );
}

export const SourcingIntelligenceTab = memo(SourcingIntelligenceTabBase);
